// frontend/src/components/planner/AddItemModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, DollarSign, Percent, CreditCard } from 'lucide-react';
import api from '../../utils/api';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';

// Static color mapping — Tailwind PurgeCSS requires complete class strings at build time
const COLOR_STYLES = {
  orange: { bg: 'bg-orange-600', hover: 'hover:bg-orange-700' },
  red:    { bg: 'bg-red-600',    hover: 'hover:bg-red-700' },
  emerald:{ bg: 'bg-emerald-600',hover: 'hover:bg-emerald-700' },
};

const FREQUENCIES = [
  { value: 'once', label: 'One-time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

export default function AddItemModal({ itemType = 'bill', onClose, onSuccess }) {
  const { activeFilter, entities } = useBusinessEntity();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [nextDueDate, setNextDueDate] = useState(getTodayString());
  const [dayOfMonth, setDayOfMonth] = useState('');
  const [isVariable, setIsVariable] = useState(false);
  const [isEnvelope, setIsEnvelope] = useState(false);
  const [startDate, setStartDate] = useState(getTodayString());
  const [bankAccountId, setBankAccountId] = useState('');

  // Entity defaults from the active filter: numeric id pre-selects that entity;
  // 'all' or 'personal' pre-selects Personal (empty string = null on submit).
  const defaultEntityId = (() => {
    if (activeFilter === 'all' || activeFilter === 'personal') return '';
    const asNum = parseInt(activeFilter, 10);
    return Number.isFinite(asNum) ? String(asNum) : '';
  })();
  const [businessEntityId, setBusinessEntityId] = useState(defaultEntityId);

  // Accounts filtered to the selected entity. business_accounts.bank_account_id
  // is UNIQUE, so each account has at most one entity; accounts without a link
  // are Personal.
  const filteredAccounts = useMemo(() => {
    const targetEntityId = businessEntityId === '' ? null : parseInt(businessEntityId, 10);
    return accounts.filter(a => (a.business_entity_id ?? null) === targetEntityId);
  }, [accounts, businessEntityId]);

  function handleEntityChange(e) {
    setBusinessEntityId(e.target.value);
    setBankAccountId(''); // reset to force a fresh pick from the newly-filtered list
  }
  
  // Debt-specific fields
  const [interestRate, setInterestRate] = useState('');
  const [remainingBalance, setRemainingBalance] = useState('');
  const [minimumPayment, setMinimumPayment] = useState('');
  const [debtSubtype, setDebtSubtype] = useState('');
  const [originalBalance, setOriginalBalance] = useState('');
  const [maturityDate, setMaturityDate] = useState('');

  // Debt subtypes configuration
  const DEBT_SUBTYPES = [
    { value: 'credit_card', label: 'Credit Card', isRevolving: true },
    { value: 'auto_loan', label: 'Auto Loan', isRevolving: false },
    { value: 'student_loan', label: 'Student Loan', isRevolving: false },
    { value: 'mortgage', label: 'Mortgage', isRevolving: false },
    { value: 'personal_loan', label: 'Personal Loan', isRevolving: false },
    { value: 'medical_debt', label: 'Medical Debt', isRevolving: false },
    { value: 'other', label: 'Other', isRevolving: false }
  ];

  const isRevolvingDebt = DEBT_SUBTYPES.find(d => d.value === debtSubtype)?.isRevolving ?? false;

  // Load accounts and categories on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [accountsRes, categoriesRes] = await Promise.all([
          api.get('/api/planner/accounts'),
          api.get('/api/household/categories')
        ]);
        
        if (accountsRes.data.success) {
          setAccounts(accountsRes.data.accounts);
        }
        if (categoriesRes.data.categories) {
          setCategories(categoriesRes.data.categories);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      }
    }
    loadData();
  }, []);

  function getTodayString() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  const typeLabels = {
    bill: { 
      title: 'Add Bill', 
      subtitle: 'Add a recurring expense (rent, utilities, subscriptions, insurance)', 
      color: 'orange' 
    },
    debt: { 
      title: 'Add Debt', 
      subtitle: 'Track a debt with interest (credit cards, loans, payment plans)', 
      color: 'red' 
    },
    income: { 
      title: 'Add Income', 
      subtitle: 'Add an income source (paycheck, freelance, side gig)', 
      color: 'emerald' 
    }
  };

  const { title, subtitle, color } = typeLabels[itemType] || typeLabels.bill;
  const colorStyles = COLOR_STYLES;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // For variable items, require start_date; for fixed items, require next_due_date
    if (isVariable) {
      if (!startDate) {
        setError('Start date is required for variable items');
        return;
      }
    } else {
      if (!nextDueDate) {
        setError('Next due date is required');
        return;
      }
    }

    // Debt validation
    if (itemType === 'debt') {
      if (!debtSubtype) {
        setError('Please select a debt type');
        return;
      }
      const rate = parseFloat(interestRate);
      const balance = parseFloat(remainingBalance);
      
      if (isNaN(rate) || rate < 0) {
        setError('Please enter a valid interest rate');
        return;
      }
      if (isNaN(balance) || balance < 0) {
        setError('Please enter a valid remaining balance');
        return;
      }
    }

    // Account validation - required
    if (!bankAccountId) {
      setError('Please select which account pays this');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        item_type: itemType,
        name: name.trim(),
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        amount: amountNum,
        frequency,
        is_variable: isVariable,
        is_envelope: isEnvelope,
        next_due_date: isVariable ? null : nextDueDate,
        start_date: isVariable ? startDate : null,
        day_of_month: isVariable ? null : (dayOfMonth ? parseInt(dayOfMonth, 10) : null),
        bank_account_id: parseInt(bankAccountId, 10),
        business_entity_id: businessEntityId ? parseInt(businessEntityId, 10) : null
      };

      // Add debt fields
      if (itemType === 'debt') {
        payload.interest_rate = parseFloat(interestRate) || 0;
        payload.remaining_balance = parseFloat(remainingBalance) || 0;
        payload.minimum_payment = parseFloat(minimumPayment) || amountNum;
        payload.debt_subtype = debtSubtype;
        if (originalBalance) {
          payload.original_balance = parseFloat(originalBalance);
        }
        if (maturityDate) {
          payload.maturity_date = maturityDate;
        }
      }

      const response = await api.post('/api/planner/recurring-items', payload);

      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.error || 'Failed to create item');
      }
    } catch (err) {
      console.error('Error creating item:', err);
      setError(err.response?.data?.error || 'Failed to create item');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center sm:p-4 text-center sm:items-center">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        <div className="relative w-full min-h-full sm:min-h-0 sm:my-8 sm:w-full sm:max-w-lg sm:max-h-[90vh] bg-white text-left shadow-xl transition-all rounded-none sm:rounded-lg sm:overflow-hidden flex flex-col">
          {/* Header — sticky on mobile so close/title stays visible while the form scrolls with the page */}
          <div className={`${colorStyles[color]?.bg || 'bg-emerald-600'} px-4 sm:px-6 py-4 flex-shrink-0 sticky top-0 z-10 sm:static`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">{title}</h3>
                <p className="text-sm text-white/80">{subtitle}</p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Form — natural height on mobile (no inner scroll); capped + scrollable on desktop */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:overflow-y-auto sm:flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={itemType === 'income' ? 'e.g., Paycheck' : itemType === 'debt' ? 'e.g., Chase Credit Card' : 'e.g., Netflix'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Entity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity
              </label>
              <select
                value={businessEntityId}
                onChange={handleEntityChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Personal</option>
                {entities.map(ent => (
                  <option key={ent.id} value={String(ent.id)}>{ent.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Items on "Personal" stay off business books.
              </p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select a category...</option>
                {categories
                  .filter(cat => {
                    // For income items, only show "Income" category
                    if (itemType === 'income') {
                      return cat.name === 'Income';
                    }
                    // For bills/debts, hide "Income" category
                    return cat.name !== 'Income';
                  })
                  .map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
              </select>
            </div>

            {/* Bank Account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {itemType === 'income' ? 'Deposits To' : 'Paid From'} <span className="text-red-500">*</span>
              </label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              >
                <option value="">Select an account...</option>
                {filteredAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.displayNameWithInstitution || acc.displayName}</option>
                ))}
              </select>
              {accounts.length === 0 ? (
                <p className="text-xs text-amber-600 mt-1">No accounts available. Connect a bank account first.</p>
              ) : filteredAccounts.length === 0 ? (
                <p className="text-xs text-amber-600 mt-1">No accounts linked to this entity. Link one from Linked Accounts.</p>
              ) : null}
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {itemType === 'debt' ? 'Payment Amount' : 'Amount'}
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  placeholder="0.00"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Variable Timing Toggle */}
            <div className="flex items-center gap-3 py-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isVariable}
                  onChange={(e) => setIsVariable(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
              <div>
                <span className="text-sm font-medium text-gray-700">Variable timing</span>
                <p className="text-xs text-gray-500">Date isn't fixed (e.g., haircuts, groceries)</p>
              </div>
            </div>

            {/* Budget Envelope Toggle - only for bills */}
            {itemType === 'bill' && (
              <div className="flex items-center gap-3 py-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEnvelope}
                    onChange={(e) => setIsEnvelope(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
                <div>
                  <span className="text-sm font-medium text-gray-700">Budget envelope</span>
                  <p className="text-xs text-gray-500">Track actual spending against this budget (e.g., $700 food allowance)</p>
                </div>
              </div>
            )}

            {/* Next Due Date - only for fixed items */}
            {!isVariable && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Next Due Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={nextDueDate}
                    onChange={(e) => setNextDueDate(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
            )}

            {/* Start Date - only for variable items */}
            {isVariable && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">When to start including in projections</p>
              </div>
            )}

            {/* Day of Month for Monthly - only for fixed items */}
            {!isVariable && frequency === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Day of Month (optional)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  placeholder="e.g., 15"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to use the date from "Next Due Date"
                </p>
              </div>
            )}

            {/* Debt-specific fields */}
            {itemType === 'debt' && (
              <>
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Debt Details
                  </h4>
                  
                  {/* Debt Type */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Debt Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={debtSubtype}
                      onChange={(e) => setDebtSubtype(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      required
                    >
                      <option value="">Select debt type...</option>
                      {DEBT_SUBTYPES.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {isRevolvingDebt ? 'Revolving debt with compound interest' : debtSubtype ? 'Fixed-term loan with simple interest' : ''}
                    </p>
                  </div>

                  {/* Interest Rate */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interest Rate (APR)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={interestRate}
                        onChange={(e) => setInterestRate(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        placeholder="e.g., 24.99"
                        className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  {/* Remaining Balance */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Balance
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={remainingBalance}
                        onChange={(e) => setRemainingBalance(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        placeholder="e.g., 5000.00"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  {/* Minimum Payment */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Payment (optional)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={minimumPayment}
                        onChange={(e) => setMinimumPayment(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        placeholder="Uses payment amount if blank"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  {/* Installment Loan Fields - only show for non-revolving debt */}
                  {debtSubtype && !isRevolvingDebt && (
                    <div className="border-t border-gray-100 pt-3 mt-3">
                      <p className="text-xs text-gray-500 mb-2">Optional: Track loan progress</p>
                      
                      {/* Original Balance */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Original Loan Amount
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={originalBalance}
                            onChange={(e) => setOriginalBalance(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            placeholder="e.g., 25000.00"
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </div>
                      </div>

                      {/* Maturity Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payoff Date
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="date"
                            value={maturityDate}
                            onChange={(e) => setMaturityDate(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] text-base sm:text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`w-full sm:w-auto px-4 py-3 sm:py-2 ${colorStyles[color]?.bg || 'bg-emerald-600'} text-white rounded-lg ${colorStyles[color]?.hover || 'hover:bg-emerald-700'} disabled:opacity-50 transition-colors min-h-[44px] text-base sm:text-sm font-medium`}
              >
                {loading ? 'Adding...' : `Add ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
